import React, { useState } from 'react';
import { Check, Crown, MapPin, Info } from 'lucide-react';
import { branchPlans, admissionFee } from '../data/mock';

export default function Plans() {
  const [activeBranch, setActiveBranch] = useState('2.0');
  const current = branchPlans[activeBranch];

  return (
    <section id="plans" className="relative bg-white py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <p className="section-eyebrow text-blue-600">03 — Membership Plans</p>
          <h2 className="mt-4 font-anton text-3xl sm:text-4xl md:text-6xl leading-[0.95] text-slate-900">
            AFFORDABLE. <span className="text-blue-600">TRANSPARENT.</span>
          </h2>
          <p className="mt-4 text-sm md:text-base text-slate-600">No hidden fees. Pick a branch, choose a plan and start training today.</p>
        </div>

        {/* Branch toggle */}
        <div className="mt-10 flex justify-center">
          <div className="inline-flex items-center gap-1 p-1.5 bg-slate-100 rounded-full ring-1 ring-slate-200 max-w-full flex-wrap justify-center">
            {Object.keys(branchPlans).map((key) => {
              const isActive = activeBranch === key;
              const b = branchPlans[key];
              return (
                <button
                  key={key}
                  onClick={() => setActiveBranch(key)}
                  className={`inline-flex items-center gap-2 px-4 md:px-5 py-2.5 rounded-full text-xs md:text-sm font-semibold transition-all whitespace-nowrap ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                      : 'text-slate-700 hover:text-slate-900'
                  }`}
                >
                  <MapPin className="w-3.5 h-3.5" />
                  {b.branchName}
                </button>
              );
            })}
          </div>
        </div>

        {/* Branch info + admission fee notice */}
        <div className="mt-6 text-center">
          <p className="text-sm text-slate-500">
            Showing pricing for <span className="font-semibold text-slate-800">{current.branchName}</span> · {current.location}
          </p>
        </div>

        {/* Plans grid */}
        <div className="mt-8 grid md:grid-cols-3 gap-6 md:gap-5 items-start">
          {current.plans.map((p, i) => {
            const isHighlight = p.highlight;
            return (
              <div
                key={`${activeBranch}-${p.duration}`}
                className={`relative rounded-2xl p-7 md:p-8 transition-all duration-300 hover:-translate-y-1 fade-up ${
                  isHighlight
                    ? 'bg-slate-950 text-white ring-1 ring-amber-500/40 shadow-2xl shadow-blue-900/20 md:-mt-4 md:mb-0'
                    : 'bg-white text-slate-900 ring-1 ring-slate-200 hover:ring-blue-300 hover:shadow-xl'
                }`}
                style={{ animationDelay: `${i * 80}ms` }}
              >
                {isHighlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-amber-500 text-slate-950 text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                    <Crown className="w-3.5 h-3.5" />
                    {p.badge}
                  </div>
                )}
                <p className={`font-anton text-xl tracking-wide ${isHighlight ? 'text-amber-400' : 'text-blue-600'}`}>{p.duration}</p>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-anton text-6xl md:text-7xl leading-none">₹{p.price}</span>
                  <span className={`text-sm ${isHighlight ? 'text-white/60' : 'text-slate-500'}`}>{p.period}</span>
                </div>

                <ul className="mt-6 space-y-2.5">
                  {p.perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-2.5 text-sm">
                      <span className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${isHighlight ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-50 text-blue-600'}`}>
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </span>
                      <span className={isHighlight ? 'text-white/85' : 'text-slate-700'}>{perk}</span>
                    </li>
                  ))}
                </ul>

                <button
                  className={`mt-7 w-full py-3 rounded-full font-semibold transition-all ${
                    isHighlight
                      ? 'bg-amber-500 hover:bg-amber-400 text-slate-950 shadow-lg shadow-amber-500/30'
                      : 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/25'
                  }`}
                >
                  {p.cta}
                </button>
              </div>
            );
          })}
        </div>

        {/* Admission fee notice */}
        <div className="mt-10 max-w-2xl mx-auto flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4">
          <span className="w-9 h-9 rounded-full bg-amber-500 text-slate-950 flex items-center justify-center flex-shrink-0">
            <Info className="w-4 h-4" strokeWidth={2.5} />
          </span>
          <p className="text-sm text-slate-800">
            <span className="font-bold">One-time admission fee: ₹{admissionFee}</span>
            <span className="text-slate-600"> · Applicable for all new members across both branches.</span>
          </p>
        </div>
      </div>
    </section>
  );
}
